import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { usePractitionersLocations } from 'api/queries/marketing-capabilities/practitioners/usePractitionersLocations'
import { usePractitionersOus } from 'api/queries/marketing-capabilities/practitioners/usePractitionersSkills'
import { useSkills } from 'api/queries/marketing-capabilities/skills/useSkills'
import { communitySortOptions, SortValue } from 'pages/exploreLearnings/constants/sortOptions.constants'

import { Button } from './Button'
import { SelectMultiple } from './SelectMultiple'
import { SortButton } from './SortButton'

interface CommunityFiltersProps {
  ouState: string[]
  locationState: string[]
  skillState: string[]
  onApplyFilters: (ous: string[], locations: string[], skills: string[]) => void
  onClearAllFilters: () => void
  onSort: (value: string) => void
}

export const CommunityFilters = ({
  ouState,
  locationState,
  skillState,
  onApplyFilters,
  onClearAllFilters,
  onSort,
}: CommunityFiltersProps) => {
  const [tempOus, setTempOus] = useState<string[]>(ouState)
  const [tempLocations, setTempLocations] = useState<string[]>(locationState)
  const [tempSkills, setTempSkills] = useState<string[]>(skillState)

  const emptyFilters = tempOus.length === 0 && tempLocations.length === 0 && tempSkills.length === 0

  const { data: skillList } = useSkills(true)
  const { data: ouList } = usePractitionersOus()
  const { data: locationList } = usePractitionersLocations()

  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    let ous = ouState
    if (ouList) {
      const validIds = ouList.map((ou: any) => ou.id)
      ous = ous.filter(id => validIds.includes(id))
    }
    setTempOus(ous)
    setTempLocations(locationState)
    setTempSkills(skillState)
  }, [ouState, locationState, skillState, ouList])

  useEffect(() => {
    onSort(SortValue.SameOUFirst)
  }, [onSort])

  const validOuIds = ouList ? ouList.map((ou: any) => ou.id) : []
  const effectiveOuState = ouState.filter(id => validOuIds.includes(id))

  const hasSameFilters =
    tempOus.length === effectiveOuState.length &&
    tempOus.every(ou => effectiveOuState.includes(ou)) &&
    tempLocations.length === locationState.length &&
    tempLocations.every(location => locationState.includes(location)) &&
    tempSkills.length === skillState.length &&
    tempSkills.every(skill => skillState.includes(skill))

  const getSkillLabel = (id: string) =>
    (Array.isArray(skillList) ? skillList : skillList?.data)?.find((skill: any) => skill.id === id)?.name || id

  const handleApply = () => {
    onApplyFilters(tempOus, tempLocations, tempSkills)
    const params = new URLSearchParams(location.search)
    // Preserve onboarding filter from current URL if it exists
    const currentParams = new URLSearchParams(location.search)
    const currentOnboarding = currentParams.get('onboarding')
    if (currentOnboarding) {
      params.set('onboarding', currentOnboarding)
    }

    const skillLabels = tempSkills.map(skillId => getSkillLabel(skillId))
    if (skillLabels.length > 0) {
      params.set('skills', skillLabels.join(','))
    } else {
      params.delete('skills')
    }

    const onboardingParam = params.get('onboarding')
    if (onboardingParam === 'true') {
      params.set('onboarding', 'true')
    } else {
      params.delete('onboarding')
    }

    const search = params.toString()
    navigate(`/explore-learnings/community${search ? `?${search}` : ''}`, { replace: true })
  }

  const handleClear = () => {
    setTempOus([])
    setTempLocations([])
    setTempSkills([])
    onClearAllFilters()

    navigate('/explore-learnings/community', { replace: true })
  }

  return (
    <div className="w-[1120px] mx-auto pt-12 pb-8 font-TCCC">
      <div className="flex justify-between">
        <div className="flex">
          {ouList && (
            <SelectMultiple
              options={
                ouList?.map((ou: any) => {
                  return { key: ou.name, value: ou.id }
                }) || []
              }
              selectValue={tempOus}
              onChange={(OU: string[]) => {
                setTempOus(OU)
              }}
              placeholder="OU"
              searchPlaceholder="E.g. EU"
            />
          )}
          {locationList && (
            <div className="mx-2">
              <SelectMultiple
                options={
                  locationList?.map((location: any) => {
                    return { key: location, value: location }
                  }) || []
                }
                selectValue={tempLocations}
                onChange={(location: string[]) => {
                  setTempLocations(location)
                }}
                placeholder="Location"
                searchPlaceholder="E.g. Spain"
              />
            </div>
          )}
          {skillList && (
            <div className="mr-2">
              <SelectMultiple
                options={
                  (Array.isArray(skillList) ? skillList : skillList.data)?.map((skill: any) => {
                    return { key: skill.name, value: skill.id }
                  }) || []
                }
                selectValue={tempSkills}
                onChange={(skills: string[]) => {
                  setTempSkills(skills)
                }}
                placeholder="Skill"
                searchPlaceholder="E.g. Science"
              />
            </div>
          )}
          <Button
            text="Apply"
            onClick={handleApply}
            disabled={hasSameFilters}
            className="mx-2 px-[24px] py-[13px] leading-none disabled:bg-[#D4D4D4] disabled:text-[#505050]"
          />
          <button
            className={`${emptyFilters ? 'text-[#9B9B9B]' : ''} font-semibold ml-2  font-TCCCText`}
            onClick={handleClear}
            disabled={emptyFilters}
          >
            Clear all
          </button>
        </div>
        <SortButton options={communitySortOptions} onChange={onSort} />
      </div>
    </div>
  )
}
